import { useCallback, useEffect, useRef, useState } from 'react';
import { getErrorMessage } from '../../../../services/apiService';
import { queryCache } from '../../../../services/queryCache';

/** How long a shared answer counts as fresh. An older one is still drawn at once, and refreshed behind it. */
export const DEFAULT_MAX_AGE_MS = 2 * 60_000;

const sameDeps = (a, b) => Array.isArray(a) && a.length === b.length && a.every((v, i) => Object.is(v, b[i]));

/**
 * Minimal fetch-state hook for the CampaignOS screens.
 *
 *   const { data, loading, error, refetch } = useApiData(() => campaignService.summary(), [], { cacheKey: 'campaign:summary' });
 *
 * - `fetcher` runs on mount and whenever `deps` change (pass the filter values you use).
 * - Stale responses are ignored (a filter change mid-flight never overwrites newer data).
 * - `error` is a user-readable string from apiService.getErrorMessage.
 * - `refetch()` is a STABLE callback, safe to put in usePageChrome dependency arrays
 *   (an unstable function there re-fires the chrome effect every render → infinite loop).
 * - Keeps the previous `data` while re-fetching so tables don't flash empty.
 *
 * With a `cacheKey` the answer is shared across screens (services/queryCache). Opening a page a
 * second time draws what was loaded before, immediately, and the server is asked again only when
 * that answer is older than `maxAgeMs`, when `refetch()` is called, or when `deps` change while the
 * key stays the same. Every write clears the cache (apiService), so nothing on screen predates the
 * last change. The key must name everything the fetcher reads, e.g. `audience:${JSON.stringify(params)}`.
 */
export function useApiData(fetcher, deps = [], { enabled = true, cacheKey = null, maxAgeMs = DEFAULT_MAX_AGE_MS } = {}) {
  const [state, setState] = useState(() => {
    const held = cacheKey ? queryCache.peek(cacheKey) : null;
    return { data: held ? held.data : null, loading: enabled && !held, error: null };
  });
  const [tick, setTick] = useState(0);
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;
  const lastRun = useRef(null);

  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;

    // A refresh someone asked for goes to the server: refetch(), or new deps under the same key.
    // Re-running with nothing changed (React's development double-run, re-enabling) does not.
    const prev = lastRun.current;
    lastRun.current = { tick, key: cacheKey, deps };
    const force = prev !== null && (prev.tick !== tick || (prev.key === cacheKey && !sameDeps(prev.deps, deps)));

    let request;
    if (cacheKey) {
      const held = queryCache.peek(cacheKey);
      const fresh = !force && held !== null && Date.now() - held.at <= maxAgeMs;
      setState((s) => ({ data: !force && held ? held.data : s.data, loading: !fresh, error: null }));
      request = queryCache.fetch(cacheKey, () => fetcherRef.current(), { maxAgeMs, force });
    } else {
      setState((s) => ({ ...s, loading: true, error: null }));
      request = Promise.resolve().then(() => fetcherRef.current());
    }

    request
      .then((res) => {
        if (!cancelled) setState({ data: res, loading: false, error: null });
      })
      .catch((err) => {
        if (!cancelled) setState((s) => ({ data: s.data, loading: false, error: getErrorMessage(err) || 'Request failed' }));
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tick, enabled, cacheKey, ...deps]);

  const refetch = useCallback(() => setTick((t) => t + 1), []);
  return { ...state, refetch };
}

export default useApiData;
